// ./src/components/features/ServiceIntervalsForm/ServiceIntervalsForm.tsx
//
// Service intervals management form with cards-only layout.
//
// Architecture:
// - Reads data from ServiceIntervalAccountsContext (provided by the page)
// - Cards-only view — single column, grouped by category with section headers
// - No accordion — all categories visible at once for easy scanning
// - Inline editing with save/reset per card
//
// Context support:
// - When rendered inside a <ServiceIntervalAccountsProvider>, data and
//   operations are read from context automatically.
// - Props can still override context values if provided.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack,
  Text,
  Box,
  Group,
  NumberInput,
  Card,
  Badge,
  Alert,
  Divider,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  ServiceIntervalAccount,
  ServiceIntervalAccountInput,
} from "@/utils/api";
import { useToast } from "@/hooks";
import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useOptionalServiceIntervalAccountsContext } from "@/contexts/ServiceIntervalAccountsContext";
import { AppButton, AppIcon, EmptyState } from "@/components/generic";
import { ServiceIntervalTypeSelector } from "@/components/generic/ServiceIntervalTypeSelector/ServiceIntervalTypeSelector";
import {
  ServiceIntervalType,
  SERVICE_INTERVAL_TYPES,
} from "@/config/serviceIntervalTypes";
import { ExpenseCategory, ExpenseKind } from "@/utils/api/AppEssentialsService";

// ============================================
// Types
// ============================================

export interface ServiceIntervalsFormProps {
  /** Car ID to manage intervals for - required */
  carId: string;

  /** Search query to filter intervals by service name */
  searchQuery?: string;

  /** Callback when an interval is saved successfully */
  onSaveSuccess?: (interval: ServiceIntervalAccount) => void;

  /** Callback when an interval is reset successfully */
  onResetSuccess?: (interval: ServiceIntervalAccount) => void;

  /** External error message to display */
  error?: string | null;
}

interface IntervalRowState {
  intervalType: ServiceIntervalType;
  mileageInterval: number | string;
  daysInterval: number | string;
  isDirty: boolean;
}

// ============================================
// Loading Skeleton
// ============================================

const CardSkeletonItem: React.FC = () => (
  <Card withBorder padding="sm" radius="sm">
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Box h={18} w={160} bg="gray.2" style={{ borderRadius: 4 }} />
        <Box h={20} w={70} bg="gray.2" style={{ borderRadius: 12 }} />
      </Group>
      <Box h={36} bg="gray.1" style={{ borderRadius: 4 }} />
      <Group grow>
        <Stack gap={4}>
          <Box h={14} w={80} bg="gray.2" style={{ borderRadius: 4 }} />
          <Box h={36} bg="gray.1" style={{ borderRadius: 4 }} />
        </Stack>
        <Stack gap={4}>
          <Box h={14} w={40} bg="gray.2" style={{ borderRadius: 4 }} />
          <Box h={36} bg="gray.1" style={{ borderRadius: 4 }} />
        </Stack>
      </Group>
    </Stack>
  </Card>
);

const LoadingSkeleton: React.FC = () => (
  <Stack gap="lg">
    {/* First category skeleton */}
    <Stack gap="sm">
      <Group gap="xs" align="center">
        <Box h={18} w={120} bg="gray.2" style={{ borderRadius: 4 }} />
        <Box h={20} w={24} bg="gray.2" style={{ borderRadius: 12 }} />
      </Group>
      {Array.from({ length: 4 }).map((_, i) => (
        <CardSkeletonItem key={`a-${i}`} />
      ))}
    </Stack>

    <Divider />

    {/* Second category skeleton */}
    <Stack gap="sm">
      <Group gap="xs" align="center">
        <Box h={18} w={100} bg="gray.2" style={{ borderRadius: 4 }} />
        <Box h={20} w={24} bg="gray.2" style={{ borderRadius: 12 }} />
      </Group>
      {Array.from({ length: 3 }).map((_, i) => (
        <CardSkeletonItem key={`b-${i}`} />
      ))}
    </Stack>
  </Stack>
);

// ============================================
// Service Interval Card Component
// ============================================

interface ServiceIntervalCardProps {
  interval: ServiceIntervalAccount;
  expenseKind: ExpenseKind | undefined;
  distanceUnit: string;
  rowState: IntervalRowState;
  isSaving: boolean;
  isResetting: boolean;
  onIntervalTypeChange: (
    kindId: number,
    value: ServiceIntervalType | null
  ) => void;
  onMileageChange: (kindId: number, value: number | string) => void;
  onDaysChange: (kindId: number, value: number | string) => void;
  onSave: (params: ServiceIntervalAccountInput) => Promise<void>;
  onReset: (carId: string, kindId: number) => Promise<void>;
}

const ServiceIntervalCardComponent: React.FC<ServiceIntervalCardProps> = ({
  interval,
  expenseKind,
  distanceUnit,
  rowState,
  isSaving,
  isResetting,
  onIntervalTypeChange,
  onMileageChange,
  onDaysChange,
  onSave,
  onReset,
}) => {
  const { t } = useTranslation(["serviceIntervals", "common"]);

  const showMileage =
    rowState.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE ||
    rowState.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS;

  const showDays =
    rowState.intervalType === SERVICE_INTERVAL_TYPES.DAYS ||
    rowState.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS;

  const isDisabled = isSaving || isResetting;

  const handleSave = useCallback(async () => {
    if (!interval.carId || interval.kindId == null) return;

    await onSave({
      carId: interval.carId,
      kindId: interval.kindId,
      intervalType: rowState.intervalType,
      mileageInterval:
        typeof rowState.mileageInterval === "number"
          ? rowState.mileageInterval
          : 0,
      daysInterval:
        typeof rowState.daysInterval === "number" ? rowState.daysInterval : 0,
    });
  }, [interval.carId, interval.kindId, rowState, onSave]);

  const handleReset = useCallback(async () => {
    if (!interval.carId || interval.kindId == null) return;
    await onReset(interval.carId, interval.kindId);
  }, [interval.carId, interval.kindId, onReset]);

  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap="sm">
        {/* Header with kind name and status badge */}
        <Group justify="space-between" align="center">
          <Text fw={500} size="sm">
            {expenseKind?.name ||
              t("serviceIntervals:form.unknownKind", {
                id: interval.kindId,
              })}
          </Text>
          {interval.isCustomized && (
            <Badge size="xs" variant="light" color="blue">
              {t("serviceIntervals:form.customized")}
            </Badge>
          )}
        </Group>

        {/* Interval Type Selector */}
        <ServiceIntervalTypeSelector
          value={rowState.intervalType}
          onChange={(value) => onIntervalTypeChange(interval.kindId!, value)}
          label={undefined}
          placeholder={t("serviceIntervals:form.selectIntervalType")}
          size="sm"
          disabled={isDisabled}
        />

        {/* Mileage and Days inputs */}
        {(showMileage || showDays) && (
          <Group grow>
            {showMileage && (
              <NumberInput
                label={t("serviceIntervals:form.distanceLabel", {
                  unit: distanceUnit,
                })}
                placeholder={t("serviceIntervals:form.enterDistance")}
                value={rowState.mileageInterval}
                onChange={(value) => onMileageChange(interval.kindId!, value)}
                disabled={isDisabled}
                min={0}
                step={1000}
                size="sm"
              />
            )}
            {showDays && (
              <NumberInput
                label={t("serviceIntervals:form.daysLabel")}
                placeholder={t("serviceIntervals:form.enterDays")}
                value={rowState.daysInterval}
                onChange={(value) => onDaysChange(interval.kindId!, value)}
                disabled={isDisabled}
                min={0}
                step={30}
                size="sm"
              />
            )}
          </Group>
        )}

        {/* Action buttons */}
        <Group justify="flex-end" gap="xs">
          {interval.isCustomized && (
            <AppButton
              variant="secondary"
              size="xs"
              onClick={handleReset}
              inProgress={isResetting}
              disabled={isSaving}
              iconName="Refresh"
            >
              {t("serviceIntervals:actions.reset")}
            </AppButton>
          )}
          {rowState.isDirty && (
            <AppButton
              size="xs"
              onClick={handleSave}
              inProgress={isSaving}
              disabled={isResetting}
              iconName="Check"
            >
              {t("serviceIntervals:actions.save")}
            </AppButton>
          )}
        </Group>
      </Stack>
    </Card>
  );
};

const ServiceIntervalCard = React.memo(ServiceIntervalCardComponent);

// ============================================
// Category Section Component
// ============================================

interface CategorySectionProps {
  category: ExpenseCategory;
  intervals: ServiceIntervalAccount[];
  distanceUnit: string;
  rowStates: Record<number, IntervalRowState>;
  isSaving: boolean;
  removingKindId: number | null;
  getExpenseKindById: (
    id: string | number | undefined
  ) => ExpenseKind | undefined;
  onIntervalTypeChange: (
    kindId: number,
    value: ServiceIntervalType | null
  ) => void;
  onMileageChange: (kindId: number, value: number | string) => void;
  onDaysChange: (kindId: number, value: number | string) => void;
  onSave: (params: ServiceIntervalAccountInput) => Promise<void>;
  onReset: (carId: string, kindId: number) => Promise<void>;
}

const CategorySectionComponent: React.FC<CategorySectionProps> = ({
  category,
  intervals,
  distanceUnit,
  rowStates,
  isSaving,
  removingKindId,
  getExpenseKindById,
  onIntervalTypeChange,
  onMileageChange,
  onDaysChange,
  onSave,
  onReset,
}) => {
  return (
    <Stack gap="sm">
      {/* Category header */}
      <Group gap="xs">
        <Text fw={500} size="md">
          {category.name}
        </Text>
        <Badge size="md" variant="light" color="gray">
          {intervals.length}
        </Badge>
      </Group>

      {/* Cards */}
      {intervals.map((interval) => {
        const kindId = interval.kindId;
        if (kindId == null) return null;

        const rowState = rowStates[kindId];
        if (!rowState) return null;

        return (
          <ServiceIntervalCard
            key={kindId}
            interval={interval}
            expenseKind={getExpenseKindById(kindId)}
            distanceUnit={distanceUnit}
            rowState={rowState}
            isSaving={isSaving}
            isResetting={removingKindId === kindId}
            onIntervalTypeChange={onIntervalTypeChange}
            onMileageChange={onMileageChange}
            onDaysChange={onDaysChange}
            onSave={onSave}
            onReset={onReset}
          />
        );
      })}
    </Stack>
  );
};

const CategorySection = React.memo(CategorySectionComponent);

// ============================================
// Main Component
// ============================================

export const ServiceIntervalsForm: React.FC<ServiceIntervalsFormProps> = ({
  carId,
  searchQuery = "",
  onSaveSuccess,
  onResetSuccess,
  error: externalError,
}) => {
  const { t } = useTranslation(["serviceIntervals", "common"]);
  const toast = useToast();
  const {
    expenseCategories,
    getExpenseKindById,
    getExpenseCategoryByKindId,
    isLoading: essentialsLoading,
  } = useAppEssentialsContext();
  const { userProfile } = useUserProfileContext();

  // ── Context vs local state resolution ──
  const context = useOptionalServiceIntervalAccountsContext();

  const serviceIntervalAccounts = context?.serviceIntervalAccounts ?? [];
  const loading = context?.loading ?? false;
  const isSaving = context?.isSaving ?? false;
  const removingKindId = context?.removingKindId ?? null;
  const lastError = context?.lastError ?? null;
  const list = context?.list;
  const set = context?.set;
  const remove = context?.remove;
  const clearError = context?.clearError;

  // ============================================
  // Row States Management
  // ============================================

  const [rowStates, setRowStates] = useState<Record<number, IntervalRowState>>(
    {}
  );

  // Initialize/update row states when interval data changes
  useEffect(() => {
    setRowStates((prevStates) => {
      const newStates: Record<number, IntervalRowState> = {};

      serviceIntervalAccounts.forEach((interval) => {
        const kindId = interval.kindId;
        if (kindId == null) return;

        const existingState = prevStates[kindId];

        // If we have existing dirty state, preserve it
        // Otherwise, initialize from the interval data
        if (existingState?.isDirty) {
          newStates[kindId] = existingState;
        } else {
          newStates[kindId] = {
            intervalType:
              (interval.intervalType as ServiceIntervalType) ??
              SERVICE_INTERVAL_TYPES.NONE,
            mileageInterval: interval.mileageInterval ?? 0,
            daysInterval: interval.daysInterval ?? 0,
            isDirty: false,
          };
        }
      });

      return newStates;
    });
  }, [serviceIntervalAccounts]);

  // Row state update handlers
  const handleIntervalTypeChange = useCallback(
    (kindId: number, value: ServiceIntervalType | null) => {
      setRowStates((prev) => ({
        ...prev,
        [kindId]: {
          ...prev[kindId],
          intervalType: value ?? SERVICE_INTERVAL_TYPES.NONE,
          isDirty: true,
        },
      }));
    },
    []
  );

  const handleMileageChange = useCallback(
    (kindId: number, value: number | string) => {
      setRowStates((prev) => ({
        ...prev,
        [kindId]: {
          ...prev[kindId],
          mileageInterval: value,
          isDirty: true,
        },
      }));
    },
    []
  );

  const handleDaysChange = useCallback(
    (kindId: number, value: number | string) => {
      setRowStates((prev) => ({
        ...prev,
        [kindId]: {
          ...prev[kindId],
          daysInterval: value,
          isDirty: true,
        },
      }));
    },
    []
  );

  // Reset row state after successful save
  const resetRowDirtyState = useCallback((kindId: number) => {
    setRowStates((prev) => ({
      ...prev,
      [kindId]: {
        ...prev[kindId],
        isDirty: false,
      },
    }));
  }, []);

  // ============================================
  // Data Loading
  // ============================================

  // Load intervals when carId changes (only if context provides list)
  useEffect(() => {
    if (carId && list) {
      list({ carId });
    }
  }, [carId, list]);

  // Show toast when lastError changes
  useEffect(() => {
    if (lastError) {
      toast.error(lastError.message);
      clearError?.();
    }
  }, [lastError, toast, clearError]);

  // Show toast for external errors
  useEffect(() => {
    if (externalError) {
      toast.error(externalError);
    }
  }, [externalError, toast]);

  // ============================================
  // Group intervals by expense category
  // ============================================

  const categoriesWithIntervals = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const categoryIntervalsMap = new Map<
      number,
      {
        category: ExpenseCategory;
        intervals: ServiceIntervalAccount[];
      }
    >();

    serviceIntervalAccounts.forEach((interval) => {
      if (interval.kindId == null) return;

      // Filter by search query — match against expense kind name
      if (normalizedQuery) {
        const expenseKind = getExpenseKindById(interval.kindId);
        const kindName = expenseKind?.name?.toLowerCase() ?? "";
        if (!kindName.includes(normalizedQuery)) return;
      }

      const category = getExpenseCategoryByKindId(interval.kindId);
      if (!category || category.id == null) return;

      const existing = categoryIntervalsMap.get(category.id);
      if (existing) {
        existing.intervals.push(interval);
      } else {
        categoryIntervalsMap.set(category.id, {
          category,
          intervals: [interval],
        });
      }
    });

    // Sort by category order
    const categoryOrder = new Map<number, number>();
    expenseCategories.forEach((cat, index) => {
      if (cat.id != null) {
        categoryOrder.set(cat.id, index);
      }
    });

    return Array.from(categoryIntervalsMap.values()).sort((a, b) => {
      const orderA = categoryOrder.get(a.category.id!) ?? 999;
      const orderB = categoryOrder.get(b.category.id!) ?? 999;
      return orderA - orderB;
    });
  }, [serviceIntervalAccounts, getExpenseCategoryByKindId, getExpenseKindById, expenseCategories, searchQuery]);

  // ============================================
  // Action Handlers
  // ============================================

  const handleSave = useCallback(
    async (params: ServiceIntervalAccountInput) => {
      if (!set) return;
      clearError?.();

      const result = await set(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        toast.success(t("serviceIntervals:messages.saveSuccess"));
        resetRowDirtyState(params.kindId);
        const savedItem = (result.data as ServiceIntervalAccount[])[0];
        onSaveSuccess?.(savedItem);
      }
    },
    [set, clearError, toast, resetRowDirtyState, onSaveSuccess, t]
  );

  const handleReset = useCallback(
    async (intervalCarId: string, kindId: number) => {
      if (!remove) return;
      clearError?.();

      const result = await remove(intervalCarId, kindId);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        toast.success(t("serviceIntervals:messages.resetSuccess"));
        const defaultItem = (result.data as ServiceIntervalAccount[])[0];
        if (defaultItem) {
          onResetSuccess?.(defaultItem);
        }
      }
    },
    [remove, clearError, toast, onResetSuccess, t]
  );

  // Get distance unit from user profile
  const distanceUnit = userProfile?.distanceIn || "km";

  // Common props for category sections
  const commonProps = useMemo(
    () => ({
      distanceUnit,
      rowStates,
      isSaving,
      removingKindId,
      getExpenseKindById,
      onIntervalTypeChange: handleIntervalTypeChange,
      onMileageChange: handleMileageChange,
      onDaysChange: handleDaysChange,
      onSave: handleSave,
      onReset: handleReset,
    }),
    [
      distanceUnit,
      rowStates,
      isSaving,
      removingKindId,
      getExpenseKindById,
      handleIntervalTypeChange,
      handleMileageChange,
      handleDaysChange,
      handleSave,
      handleReset,
    ]
  );

  // ============================================
  // Render
  // ============================================

  // Loading state
  if (loading || essentialsLoading) {
    return <LoadingSkeleton />;
  }

  // No intervals state
  if (!loading && serviceIntervalAccounts.length === 0) {
    return (
      <Alert
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        color="yellow"
      >
        {t("serviceIntervals:emptyState.noServices")}
      </Alert>
    );
  }

  // Search returned no results
  const hasSearchQuery = searchQuery.trim().length > 0;
  const searchEmpty =
    hasSearchQuery && categoriesWithIntervals.length === 0;

  if (searchEmpty) {
    return (
      <EmptyState
        title={t(
          "serviceIntervals:emptyState.noSearchResults",
          "No services found"
        )}
        description={t(
          "serviceIntervals:emptyState.noSearchResultsDescription",
          "No service intervals match your search. Try a different term."
        )}
        iconName="Search"
        size="sm"
      />
    );
  }

  return (
    <Stack gap="lg">
      {categoriesWithIntervals.length > 0 ? (
        categoriesWithIntervals.map(({ category, intervals }, index) => (
          <React.Fragment key={category.id}>
            {index > 0 && <Divider />}
            <CategorySection
              category={category}
              intervals={intervals}
              {...commonProps}
            />
          </React.Fragment>
        ))
      ) : (
        // Fallback: show ungrouped if categories couldn't be determined
        <Stack gap="sm">
          {serviceIntervalAccounts.map((interval) => {
            const kindId = interval.kindId;
            if (kindId == null) return null;

            const rowState = rowStates[kindId];
            if (!rowState) return null;

            return (
              <ServiceIntervalCard
                key={kindId}
                interval={interval}
                expenseKind={getExpenseKindById(kindId)}
                distanceUnit={distanceUnit}
                rowState={rowState}
                isSaving={isSaving}
                isResetting={removingKindId === kindId}
                onIntervalTypeChange={handleIntervalTypeChange}
                onMileageChange={handleMileageChange}
                onDaysChange={handleDaysChange}
                onSave={handleSave}
                onReset={handleReset}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default ServiceIntervalsForm;